// src/Payroll_Management/PayRoll_Report/EmployeeTransferReportPrintAll.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Printer, ArrowLeft } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}

function getQS(locationSearch) {
  const p = new URLSearchParams(locationSearch);
  return {
    fromSiteId: s(p.get("fromSiteId")),
    toSiteId: s(p.get("toSiteId")),
    fromDate: s(p.get("fromDate")),
    toDate: s(p.get("toDate")),
    empCode: s(p.get("empCode")),
    empName: s(p.get("empName")),
    tranCodeId: s(p.get("tranCodeId")),
  };
}

const EmployeeTransferReportPrintAll = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";

  const filters = useMemo(() => getQS(location.search), [location.search]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // optional: company header text (static like screenshot)
  const company = useMemo(
    () => ({
      title: "Nissi Infotech",
      sub: "Nissi",
      line1: "CIVIL CONSTRUCTION",
      address:
        "3/42-12, Nagappa colony, kavundampalayam, Coimbatore - 641030. Tamil Nadu.",
      phone: "Phone No : 9003744644",
      web: "infraconsoft-implementa",
    }),
    []
  );

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) {
      alert("Financial Year not selected.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-transfer-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          fromSiteId: filters.fromSiteId || "Select",
          toSiteId: filters.toSiteId || "Select",
          fromDate: filters.fromDate || "",
          toDate: filters.toDate || "",
          empCode: filters.empCode || "",
          empName: filters.empName || "",
          tranCodeId: filters.tranCodeId || "Select",
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
      } else {
        alert(res?.data?.message || "Failed to load print data");
      }
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Print data fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrint = () => window.print();

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh", padding: 16 }}>
      {/* Screen-only toolbar */}
      <div className="no-print" style={{ maxWidth: 980, margin: "0 auto 12px auto" }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 14,
              border: "1px solid #d1d5db",
              background: "white",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={onPrint}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 14px",
                borderRadius: 14,
                border: "1px solid #111827",
                background: "#111827",
                color: "white",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              <Printer size={16} />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Printable page */}
      <div
        id="print-area"
        style={{
          maxWidth: 980,
          margin: "0 auto",
          background: "white",
          border: "1px solid #111",
          padding: 18,
        }}
      >
        {/* Header */}
        <div style={{ border: "1px solid #111", padding: 10 }}>
          <div style={{ display: "flex", gap: 12 }}>
            {/* Logo box (left) */}
            <div
              style={{
                width: 100,
                height: 78,
                border: "1px solid #111",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              LOGO
            </div>

            {/* Center company info */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{company.title}</div>
              <div style={{ fontSize: 12, marginTop: 2 }}>{company.sub}</div>
              <div style={{ fontSize: 13, fontWeight: 800, marginTop: 2 }}>{company.line1}</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>{company.address}</div>
              <div style={{ fontSize: 11, marginTop: 2 }}>
                {company.phone} &nbsp;&nbsp; {company.web}
              </div>
            </div>

            {/* Right empty box (like screenshot spacing) */}
            <div style={{ width: 100 }} />
          </div>
        </div>

        {/* Title */}
        <div
          style={{
            borderLeft: "1px solid #111",
            borderRight: "1px solid #111",
            borderBottom: "1px solid #111",
            padding: "10px 8px",
            textAlign: "center",
            fontWeight: 900,
            letterSpacing: 0.6,
          }}
        >
          EMPLOYEE TRANSFER OVERALL REPORT
        </div>

        {/* Table */}
        <div style={{ border: "1px solid #111", borderTop: "none" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  "S.No",
                  "Transfer Date",
                  "Employee Code",
                  "Employee Name",
                  "Department Name",
                  "From Branch",
                  "To Branch",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      border: "1px solid #111",
                      padding: "6px 6px",
                      fontSize: 12,
                      fontWeight: 900,
                      textAlign: "center",
                      background: "#f3f4f6",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ padding: 14, textAlign: "center", fontSize: 12 }}>
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 14, textAlign: "center", fontSize: 12 }}>
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={`${r.empid ?? idx}-${idx}`}>
                    <td style={cell("center")}>{idx + 1}</td>
                    <td style={cell("center")}>{r.trdate}</td>
                    <td style={cell("center")}>{r.code}</td>
                    <td style={cell("left")}>{r.empname}</td>
                    <td style={cell("left")}>{r.depnme}</td>
                    <td style={cell("center")}>{r.frombrnme}</td>
                    <td style={cell("center")}>{r.brnme}</td>
                    <td style={cell("center")}>{r.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Optional footer spacing */}
        <div style={{ height: 12 }} />
      </div>

      {/* Print CSS */}
      <style>{`
        @page {
          size: A4;
          margin: 12mm;
        }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          #print-area {
            max-width: none !important;
            margin: 0 !important;
            border: none !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

// Cell helper (borders like screenshot)
function cell(align = "center") {
  return {
    border: "1px solid #111",
    padding: "6px 6px",
    fontSize: 12,
    textAlign: align,
    verticalAlign: "top",
    whiteSpace: "nowrap",
  };
}

export default EmployeeTransferReportPrintAll;
